import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash, ChevronDown, ChevronUp, X, Building, Mail, Calendar, Lock, MapPin, Globe } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Selection from '../Management/Company/ManageCountryselection/selection';
import EditCityCompanymanagement from './editCityCompanymanagement';

const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
  const [form, setForm] = useState({
    company_name: '',
    company_email: '',
    company_expiry: '',
    password: '',
    permission_location: [],
  });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCities, setSelectedCities] = useState([]);
  const [temporarySelections, setTemporarySelections] = useState([]);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const cityDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // Validate form
  useEffect(() => {
    const isValid = 
      (form.company_name || '').trim() !== '' &&
      (form.company_email || '').trim() !== '' &&
      (form.company_expiry || '').trim() !== '' &&
      (form.password || '').trim() !== '' &&
      temporarySelections.length > 0;
    setIsFormValid(isValid);
  }, [form, temporarySelections]);
  
  // Pre-fill form if editingCompany is provided
  useEffect(() => {
    if (editingCompany) {
      setForm({
        company_name: editingCompany.company_name || '',
        company_email: editingCompany.company_email || '',
        company_expiry: editingCompany.company_expiry || '',
        password: editingCompany.password || '',
        permission_location: editingCompany.permission_location || [],
      });

      setTemporarySelections(editingCompany.permission_location || []);
    }
  }, [editingCompany]);

  const handleEditSelection = (index) => {
    const selection = temporarySelections[index];
    
    setSelectedCountry({ 
      value: selection.countryCode || selection.country, 
      label: selection.country 
    });
  
    setSelectedState(selection.state ? { 
      value: selection.state, 
      label: selection.state 
    } : null);
  
    setSelectedCities(
      selection.city 
        ? selection.city.split(',').map(city => ({
            value: city.trim(),
            label: city.trim()
          }))
        : []
    );
  
    setEditingIndex(index);
  };

  const handleUpdateSelection = (updatedCities) => {
    const updatedSelections = [...temporarySelections];
    updatedSelections[editingIndex] = {
      ...updatedSelections[editingIndex],
      city: updatedCities.map(c => c.label).join(', ')
    };
    setTemporarySelections(updatedSelections);
    setEditingIndex(null);
    resetFields();
  };
  const handleAddSelection = () => {
    if (!selectedCountry) {
      toast.error('Please select a country');
      return;
    }
    
    const newSelection = {
      country: selectedCountry.label,
      countryCode: selectedCountry.value,
      state: selectedState?.label || '',
      city: selectedCities.map(city => city.label).join(', '),
      cities: selectedCities.map(city => city.label) // Maintain both formats
    };

    if (editingIndex !== null) {
      const updatedSelections = [...temporarySelections];
      updatedSelections[editingIndex] = newSelection;
      setTemporarySelections(updatedSelections);
      setEditingIndex(null);
    } else {
      setTemporarySelections([...temporarySelections, newSelection]);
    }

    resetFields();
  };

  const resetFields = () => {
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCities([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid) {
      toast.error('Please fill all required fields and add at least one location');
      return;
    }

    const newCompany = {
      ...form,
      permission_location: temporarySelections.map(loc => ({
        country: loc.country,
        state: loc.state,
        city: loc.city,
        cities: loc.city.split(',').map(c => c.trim()), // Ensure array format
        countryCode: loc.countryCode
      }))
    };

    addCompanyData(newCompany);
    handleReset();
    toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
    setTimeout(() => onClose(), 1500);
  };

  const handleReset = () => {
    setForm({
      company_name: '',
      company_email: '',
      company_expiry: '',
      password: '',
      permission_location: [],
    });
    resetFields();
    setTemporarySelections([]);
  };

  const handleDeleteSelection = (index) => {
    const updatedSelections = temporarySelections.filter((_, i) => i !== index);
    setTemporarySelections(updatedSelections);
    toast.success('Location removed successfully!');
  };

  const formatDisplay = (items) => {
    if (!items || items.length === 0) return 'None selected';
    const displayedItems = items.slice(0, 4);
    return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="bg-white p-6 border-b border-gray-200 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">
            {editingCompany ? 'Edit Company' : 'Add New Company'}
          </h1>
          <button 
            onClick={onClose} 
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="text-gray-500" size={20} />
          </button>
        </div>

        {editingIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
            <div className="bg-white p-8 shadow-lg rounded-lg max-w-full w-[600px]">
              <EditCityCompanymanagement
                selectedCountry={selectedCountry}
                selectedState={selectedState}
                selectedCities={selectedCities}
                onClose={() => {
                  setEditingIndex(null);
                  resetFields();
                }}
                onSave={handleUpdateSelection}
              />
            </div>
          </div>
        )}
        
        <ToastContainer position="top-right" autoClose={2000} hideProgressBar closeButton={false} />
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">
          {/* Company Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Company Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
                  <Building size={18} />
                </div>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter company name"
                />
                {form.company_name && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, company_name: '' })}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Company Email */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Company Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  value={form.company_email}
                  onChange={(e) => setForm({ ...form, company_email: e.target.value })}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter company email"
                />
                {form.company_email && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, company_email: '' })}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Company Expiry Date */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Expiry Date
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
                  <Calendar size={18} />
                </div>
                <input
                  type="date"
                  value={form.company_expiry}
                  onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
                  className="w-full pl-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Password
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter password"
                />
                {form.password && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, password: '' })}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Location Selection */}
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="flex items-center space-x-2 mb-4">
              <Globe className="text-gray-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-800">Location Permissions</h2>
            </div>
            
            <Selection
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              selectedCities={selectedCities}
              setSelectedCities={setSelectedCities}
              isCityDropdownOpen={isCityDropdownOpen}
              setIsCityDropdownOpen={setIsCityDropdownOpen}
              cityDropdownRef={cityDropdownRef}
              isStateDropdownOpen={isStateDropdownOpen}
              setIsStateDropdownOpen={setIsStateDropdownOpen}
              stateDropdownRef={stateDropdownRef}
              handleAddSelection={handleAddSelection}
              editingIndex={editingIndex}
            />

            {/* Temporary Selections */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="text-gray-600" size={18} />
                  <h3 className="text-md font-medium text-gray-700">Selected Locations</h3>
                </div>
                <span className="text-sm text-gray-500">
                  {temporarySelections.length} location{temporarySelections.length !== 1 ? 's' : ''} added
                </span>
              </div>
              
              {temporarySelections.length === 0 ? (
                <div className="bg-white p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
                  <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Plus className="text-gray-400" size={20} />
                  </div>
                  <p className="text-gray-500">No locations added yet</p>
                  <p className="text-sm text-gray-400 mt-1">Select a country, state, and cities above</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {temporarySelections.map((selection, index) => {
                    const cities = selection.city ? selection.city.split(',') : [];
                    return (
                      <div 
                        key={index} 
                        className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-colors relative group"
                      >
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <button
                            onClick={() => handleEditSelection(index)}
                            className="p-1.5 bg-blue-50 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
                            aria-label="Edit location"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteSelection(index)}
                            className="p-1.5 bg-red-50 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                            aria-label="Delete location"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                            <MapPin size={18} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800">{selection.country}</h4>
                            <div className="mt-1 space-y-1">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">State:</span> {selection.state || 'All states'}
                              </p>
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Cities:</span> {formatDisplay(cities)}
                                {cities.length > 4 && (
                                  <span className="text-xs text-gray-400 ml-1">(+{cities.length - 4} more)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Form Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className={`px-6 py-2.5 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all ${
                isFormValid 
                  ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-blue-300 cursor-not-allowed'
              }`}
            >
              {editingCompany ? 'Update Company' : 'Add Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyManagementAdd;

// import React, { useState, useEffect, useRef } from 'react';
// import { Plus, Edit, Trash, ChevronDown, ChevronUp, X, Building, Mail, Calendar, Lock, MapPin, Globe } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';
// import EditCityCompanymanagement from './editCityCompanymanagement';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });


//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);
//   const [isFormValid, setIsFormValid] = useState(false);

//   // Validate form
//   useEffect(() => {
//     const isValid = 
//       (form.company_name || '').trim() !== '' &&
//       (form.company_email || '').trim() !== '' &&
//       (form.company_expiry || '').trim() !== '' &&
//       (form.password || '').trim() !== '' &&
//       temporarySelections.length > 0;
//     setIsFormValid(isValid);
//   }, [form, temporarySelections]);
  

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name || '',
//         company_email: editingCompany.company_email || '',
//         company_expiry: editingCompany.company_expiry || '',
//         password: editingCompany.password || '',
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         setSelectedCountry({ 
//           value: firstLocation.countryCode || firstLocation.country, 
//           label: firstLocation.country 
//         });
        
//         setSelectedState(firstLocation.state ? { 
//           value: firstLocation.state, 
//           label: firstLocation.state 
//         } : null);

//         setSelectedCities(firstLocation.cities?.map(city => ({
//           value: firstLocation.city,
//           label: firstLocation.city
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);
//   // const [selectedCountry, setSelectedCountry] = useState(null);
//   // const [selectedState, setSelectedState] = useState(null);
//   // const [selectedCities, setSelectedCities] = useState([]);
//   // const [editingIndex, setEditingIndex] = useState(null);
  
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     console.log(`this is selection ${JSON.stringify(selection)}`);
  
//     setSelectedCountry({ 
//       value: selection.countryCode || selection.country, 
//       label: selection.country 
//     });
  
//     setSelectedState(selection.state ? { 
//       value: selection.state, 
//       label: selection.state 
//     } : null);
  
//     // Fix: Split the cities string into an array and then map
//     setSelectedCities(
//       selection?.city
//         ? selection.city.split(',').map(city => ({
//             value: city.trim(),
//             label: city.trim()
//         }))
//         : []
//     );
  
//     setEditingIndex(index);
  
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };
  



//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry) {
//       toast.error('Please select a country');
//       return;
//     }
    
//     const newSelection = {
//       country: selectedCountry.label,
//       state: selectedState?.label || '',
//       cities: selectedCities.map(city => city.label)
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!isFormValid) {
//       toast.error('Please fill all required fields and add at least one location');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     setTimeout(() => onClose(), 1500);
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   // const handleEditSelection = (index) => {
//   //   const selection = temporarySelections[index];
    
//   //   setSelectedCountry({ 
//   //     value: selection.countryCode || selection.country, 
//   //     label: selection.country 
//   //   });
    
//   //   setSelectedState(selection.state ? { 
//   //     value: selection.state, 
//   //     label: selection.state 
//   //   } : null);

//   //   setSelectedCities(selection.cities?.map(city => ({
//   //     value: city,
//   //     label: city
//   //   })) || []);

//   //   setEditingIndex(index);
//   //   window.scrollTo({ top: 0, behavior: 'smooth' });
//   // };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Location removed successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//       <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
//         <div className=" bg-white p-6 border-b border-gray-200 flex justify-between items-center">
//           <h1 className="text-2xl font-bold text-gray-800">
//             {editingCompany ? 'Edit Company' : 'Add New Company'}
//           </h1>
//           <button 
//             onClick={onClose} 
//             className="p-2 rounded-full hover:bg-gray-100 transition-colors"
//             aria-label="Close"
//           >
//             <X className="text-gray-500" size={20} />
//           </button>
//         </div>

        
//         {editingIndex !== null && (
//   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
//     <div className="bg-white p-8 shadow-lg rounded-lg max-w-full w-[600px]">
//       <EditCityCompanymanagement
//         selectedCountry={selectedCountry}
//         selectedState={selectedState}
//         selectedCities={selectedCities}
//         onClose={() => setEditingIndex(null)}
//       />
//     </div>
//   </div>
// )}


        
//         <ToastContainer position="top-right" autoClose={2000} hideProgressBar closeButton={false} />
        
//         <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">
//           {/* Company Details */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             {/* Company Name */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Company Name
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Building size={18} />
//                 </div>
//                 <input
//                   type="text"
//                   value={form.company_name}
//                   onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter company name"
//                 />
//                 {form.company_name && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, company_name: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>

//             {/* Company Email */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Company Email
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Mail size={18} />
//                 </div>
//                 <input
//                   type="email"
//                   value={form.company_email}
//                   onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter company email"
//                 />
//                 {form.company_email && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, company_email: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>

//             {/* Company Expiry Date */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Expiry Date
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Calendar size={18} />
//                 </div>
//                 <input
//                   type="date"
//                   value={form.company_expiry}
//                   onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//                   className="w-full pl-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                 />
//               </div>
//             </div>

//             {/* Password */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Password
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Lock size={18} />
//                 </div>
//                 <input
//                   type="password"
//                   value={form.password}
//                   onChange={(e) => setForm({ ...form, password: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter password"
//                 />
//                 {form.password && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, password: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Location Selection */}
//           <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
//             <div className="flex items-center space-x-2 mb-4">
//               <Globe className="text-gray-600" size={20} />
//               <h2 className="text-lg font-semibold text-gray-800">Location Permissions</h2>
//             </div>
            
//             <Selection
//               selectedCountry={selectedCountry}
//               setSelectedCountry={setSelectedCountry}
//               selectedState={selectedState}
//               setSelectedState={setSelectedState}
//               selectedCities={selectedCities}
//               setSelectedCities={setSelectedCities}
//               isCityDropdownOpen={isCityDropdownOpen}
//               setIsCityDropdownOpen={setIsCityDropdownOpen}
//               cityDropdownRef={cityDropdownRef}
//               isStateDropdownOpen={isStateDropdownOpen}
//               setIsStateDropdownOpen={setIsStateDropdownOpen}
//               stateDropdownRef={stateDropdownRef}
//               handleAddSelection={handleAddSelection}
//               editingIndex={editingIndex}
//             />

//             {/* Temporary Selections */}
//             <div className="mt-6">
//               <div className="flex justify-between items-center mb-4">
//                 <div className="flex items-center space-x-2">
//                   <MapPin className="text-gray-600" size={18} />
//                   <h3 className="text-md font-medium text-gray-700">Selected Locations</h3>
//                 </div>
//                 <span className="text-sm text-gray-500">
//                   {temporarySelections.length} location{temporarySelections.length !== 1 ? 's' : ''} added
//                 </span>
//               </div>
              
//               {temporarySelections.length === 0 ? (
//                 <div className="bg-white p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
//                   <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
//                     <Plus className="text-gray-400" size={20} />
//                   </div>
//                   <p className="text-gray-500">No locations added yet</p>
//                   <p className="text-sm text-gray-400 mt-1">Select a country, state, and cities above</p>
//                 </div>
//               ) : (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   
//                   {temporarySelections.map((selection, index) => (
//                     <div 
//                       key={index} 
//                       className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-colors relative group"
//                     >
//                       <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
//                         <button
//                           onClick={() => handleEditSelection(index)}
//                           className="p-1.5 bg-blue-50 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
//                           aria-label="Edit location"
//                         >
//                           <Edit size={14} />
//                         </button>
//                         <button
//                           onClick={() => handleDeleteSelection(index)}
//                           className="p-1.5 bg-red-50 rounded-full text-red-500 hover:bg-red-100 transition-colors"
//                           aria-label="Delete location"
//                         >
//                           <Trash size={14} />
//                         </button>
//                       </div>
//                       <div className="flex items-start space-x-3">
//                         <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
//                           <MapPin size={18} />
//                         </div>
//                         <div>
//                           <h4 className="font-semibold text-gray-800">{selection.country}</h4>
//                           <div className="mt-1 space-y-1">
//                             <p className="text-sm text-gray-600">
//                               <span className="font-medium">State:</span> {selection.state || 'All states'}
//                             </p>
//                             <p className="text-sm text-gray-600">
//                               <span className="font-medium">Cities:</span> {(selection?.city || [])}
//                               {selection?.city?.length > 4 && (
//                                 <span className="text-xs text-gray-400 ml-1">(+{selection.city.length - 4} more)</span>
//                               )}
//                             </p>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Form Buttons */}
//           <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={handleReset}
//               className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
//             >
//               Reset
//             </button>
//             <button
//               type="submit"
//               disabled={!isFormValid}
//               className={`px-6 py-2.5 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all ${
//                 isFormValid 
//                   ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
//                   : 'bg-blue-300 cursor-not-allowed'
//               }`}
//             >
//               {editingCompany ? 'Update Company' : 'Add Company'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default CompanyManagementAdd;

// import React, { useState, useEffect, useRef } from 'react';
// import { Plus, Edit, Trash, ChevronDown, ChevronUp, X, Building, Mail, Calendar, Lock, MapPin, Globe } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });


//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);
//   const [isFormValid, setIsFormValid] = useState(false);

//   // Validate form
//   useEffect(() => {
//     const isValid = 
//       form.company_name.trim() !== '' &&
//       form.company_email.trim() !== '' &&
//       form.company_expiry.trim() !== '' &&
//       form.password.trim() !== '' &&
//       temporarySelections.length > 0;
//     setIsFormValid(isValid);
//   }, [form, temporarySelections]);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         setSelectedCountry({ 
//           value: firstLocation.countryCode || firstLocation.country, 
//           label: firstLocation.country 
//         });
        
//         setSelectedState(firstLocation.state ? { 
//           value: firstLocation.state, 
//           label: firstLocation.state 
//         } : null);

//         setSelectedCities(firstLocation.cities?.map(city => ({
//           value: city,
//           label: city
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry) {
//       toast.error('Please select a country');
//       return;
//     }
    
//     const newSelection = {
//       country: selectedCountry.label,
//       state: selectedState?.label || '',
//       cities: selectedCities.map(city => city.label)
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!isFormValid) {
//       toast.error('Please fill all required fields and add at least one location');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     setTimeout(() => onClose(), 1500);
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
    
//     setSelectedCountry({ 
//       value: selection.countryCode || selection.country, 
//       label: selection.country 
//     });
    
//     setSelectedState(selection.state ? { 
//       value: selection.state, 
//       label: selection.state 
//     } : null);

//     setSelectedCities(selection.cities?.map(city => ({
//       value: city,
//       label: city
//     })) || []);

//     setEditingIndex(index);
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Location removed successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//       <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
//         <div className=" bg-white p-6 border-b border-gray-200 flex justify-between items-center">
//           <h1 className="text-2xl font-bold text-gray-800">
//             {editingCompany ? 'Edit Company' : 'Add New Company'}
//           </h1>
//           <button 
//             onClick={onClose} 
//             className="p-2 rounded-full hover:bg-gray-100 transition-colors"
//             aria-label="Close"
//           >
//             <X className="text-gray-500" size={20} />
//           </button>
//         </div>
        
//         <ToastContainer position="top-right" autoClose={2000} hideProgressBar closeButton={false} />
        
//         <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">
//           {/* Company Details */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             {/* Company Name */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Company Name
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Building size={18} />
//                 </div>
//                 <input
//                   type="text"
//                   value={form.company_name}
//                   onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter company name"
//                 />
//                 {form.company_name && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, company_name: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>

//             {/* Company Email */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Company Email
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Mail size={18} />
//                 </div>
//                 <input
//                   type="email"
//                   value={form.company_email}
//                   onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter company email"
//                 />
//                 {form.company_email && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, company_email: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>

//             {/* Company Expiry Date */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Expiry Date
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Calendar size={18} />
//                 </div>
//                 <input
//                   type="date"
//                   value={form.company_expiry}
//                   onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//                   className="w-full pl-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                 />
//               </div>
//             </div>

//             {/* Password */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 Password
//                 <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-blue-500">
//                   <Lock size={18} />
//                 </div>
//                 <input
//                   type="password"
//                   value={form.password}
//                   onChange={(e) => setForm({ ...form, password: e.target.value })}
//                   className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                   placeholder="Enter password"
//                 />
//                 {form.password && (
//                   <button
//                     type="button"
//                     onClick={() => setForm({ ...form, password: '' })}
//                     className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Location Selection */}
//           <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
//             <div className="flex items-center space-x-2 mb-4">
//               <Globe className="text-gray-600" size={20} />
//               <h2 className="text-lg font-semibold text-gray-800">Location Permissions</h2>
//             </div>
            
//             <Selection
//               selectedCountry={selectedCountry}
//               setSelectedCountry={setSelectedCountry}
//               selectedState={selectedState}
//               setSelectedState={setSelectedState}
//               selectedCities={selectedCities}
//               setSelectedCities={setSelectedCities}
//               isCityDropdownOpen={isCityDropdownOpen}
//               setIsCityDropdownOpen={setIsCityDropdownOpen}
//               cityDropdownRef={cityDropdownRef}
//               isStateDropdownOpen={isStateDropdownOpen}
//               setIsStateDropdownOpen={setIsStateDropdownOpen}
//               stateDropdownRef={stateDropdownRef}
//               handleAddSelection={handleAddSelection}
//               editingIndex={editingIndex}
//             />

//             {/* Temporary Selections */}
//             <div className="mt-6">
//               <div className="flex justify-between items-center mb-4">
//                 <div className="flex items-center space-x-2">
//                   <MapPin className="text-gray-600" size={18} />
//                   <h3 className="text-md font-medium text-gray-700">Selected Locations</h3>
//                 </div>
//                 <span className="text-sm text-gray-500">
//                   {temporarySelections.length} location{temporarySelections.length !== 1 ? 's' : ''} added
//                 </span>
//               </div>
              
//               {temporarySelections.length === 0 ? (
//                 <div className="bg-white p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
//                   <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
//                     <Plus className="text-gray-400" size={20} />
//                   </div>
//                   <p className="text-gray-500">No locations added yet</p>
//                   <p className="text-sm text-gray-400 mt-1">Select a country, state, and cities above</p>
//                 </div>
//               ) : (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {temporarySelections.map((selection, index) => (
//                     <div 
//                       key={index} 
//                       className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-colors relative group"
//                     >
//                       <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
//                         <button
//                           onClick={() => handleEditSelection(index)}
//                           className="p-1.5 bg-blue-50 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
//                           aria-label="Edit location"
//                         >
//                           <Edit size={14} />
//                         </button>
//                         <button
//                           onClick={() => handleDeleteSelection(index)}
//                           className="p-1.5 bg-red-50 rounded-full text-red-500 hover:bg-red-100 transition-colors"
//                           aria-label="Delete location"
//                         >
//                           <Trash size={14} />
//                         </button>
//                       </div>
//                       <div className="flex items-start space-x-3">
//                         <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
//                           <MapPin size={18} />
//                         </div>
//                         <div>
//                           <h4 className="font-semibold text-gray-800">{selection.country}</h4>
//                           <div className="mt-1 space-y-1">
//                             <p className="text-sm text-gray-600">
//                               <span className="font-medium">State:</span> {selection.state || 'All states'}
//                             </p>
//                             <p className="text-sm text-gray-600">
//                               <span className="font-medium">Cities:</span> {formatDisplay(selection.cities || [])}
//                               {selection.cities?.length > 4 && (
//                                 <span className="text-xs text-gray-400 ml-1">(+{selection.cities.length - 4} more)</span>
//                               )}
//                             </p>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Form Buttons */}
//           <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={handleReset}
//               className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
//             >
//               Reset
//             </button>
//             <button
//               type="submit"
//               disabled={!isFormValid}
//               className={`px-6 py-2.5 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all ${
//                 isFormValid 
//                   ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
//                   : 'bg-blue-300 cursor-not-allowed'
//               }`}
//             >
//               {editingCompany ? 'Update Company' : 'Add Company'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default CompanyManagementAdd;

// new code according to selection section changes geo......... 
// import React, { useState, useEffect, useRef } from 'react';
// import { Plus, Edit, Trash } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         setSelectedCountry({ 
//           value: firstLocation.countryCode || firstLocation.country, 
//           label: firstLocation.country 
//         });
        
//         setSelectedState(firstLocation.state ? { 
//           value: firstLocation.state, 
//           label: firstLocation.state 
//         } : null);

//         setSelectedCities(firstLocation.cities?.map(city => ({
//           value: city,
//           label: city
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     const newSelection = {
//       country: selectedCountry.label,
//       state: selectedState.label,
//       cities: selectedCities.map(city => city.label)
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       toast.error('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
    
//     setSelectedCountry({ 
//       value: selection.countryCode || selection.country, 
//       label: selection.country 
//     });
    
//     setSelectedState(selection.state ? { 
//       value: selection.state, 
//       label: selection.state 
//     } : null);

//     setSelectedCities(selection.cities?.map(city => ({
//       value: city,
//       label: city
//     })) || []);

//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Selection deleted successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <ToastContainer position="top-right" autoClose={1000} />
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedState={selectedState}
//           setSelectedState={setSelectedState}
//           selectedCities={selectedCities}
//           setSelectedCities={setSelectedCities}
//           isCityDropdownOpen={isCityDropdownOpen}
//           setIsCityDropdownOpen={setIsCityDropdownOpen}
//           cityDropdownRef={cityDropdownRef}
//           isStateDropdownOpen={isStateDropdownOpen}
//           setIsStateDropdownOpen={setIsStateDropdownOpen}
//           stateDropdownRef={stateDropdownRef}
//           handleAddSelection={handleAddSelection}
//           editingIndex={editingIndex}
//         />

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>State:</strong> {selection.state || 'None selected'}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong> {formatDisplay(selection.cities || [])}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;


// import React, { useState, useEffect, useRef } from 'react';
// import { Plus, Edit, Trash, ChevronDown, ChevronUp, Check, X, MapPin, Globe, Lock, Mail, Calendar, Building } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);
//   const formRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         setSelectedCountry({ 
//           value: firstLocation.countryCode || firstLocation.country, 
//           label: firstLocation.country 
//         });
        
//         setSelectedState(firstLocation.state ? { 
//           value: firstLocation.state, 
//           label: firstLocation.state 
//         } : null);

//         setSelectedCities(firstLocation.cities?.map(city => ({
//           value: city,
//           label: city
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry) {
//       toast.error('Please select a country');
//       return;
//     }

//     const newSelection = {
//       country: selectedCountry.label,
//       countryCode: selectedCountry.value, // Add this line to include country code
//       state: selectedState?.label || '',
//       cities: selectedCities.map(city => city.label)
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//       toast.success('Location updated successfully!');
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//       toast.success('Location added successfully!');
      
//       // Animate the new card
//       const cards = document.querySelectorAll('.location-card');
//       if (cards.length > 0) {
//         const lastCard = cards[cards.length - 1];
//         lastCard.classList.add('animate-pulse');
//         setTimeout(() => {
//           lastCard.classList.remove('animate-pulse');
//         }, 1000);
//       }
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!form.company_name || !form.company_email || !form.password) {
//       toast.error('Please fill all required fields');
//       return;
//     }

//     if (temporarySelections.length === 0) {
//       toast.error('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
    
//     // Success animation
//     if (formRef.current) {
//       formRef.current.classList.add('animate-bounce');
//       setTimeout(() => {
//         if (formRef.current) formRef.current.classList.remove('animate-bounce');
//       }, 500);
//     }
    
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`, {
//       icon: '🎉'
//     });
    
//     setTimeout(() => {
//       handleReset();
//       onClose();
//     }, 1500);
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
    
//     setSelectedCountry({ 
//       value: selection.countryCode || selection.country, 
//       label: selection.country 
//     });
    
//     setSelectedState(selection.state ? { 
//       value: selection.state, 
//       label: selection.state 
//     } : null);

//     setSelectedCities(selection.cities?.map(city => ({
//       value: city,
//       label: city
//     })) || []);

//     setEditingIndex(index);
    
//     // Scroll to top with smooth animation
//     window.scrollTo({
//       top: 0,
//       behavior: 'smooth'
//     });
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     // Animation for deletion
//     const card = document.getElementById(`location-card-${index}`);
//     if (card) {
//       card.classList.add('animate-fade-out');
//       setTimeout(() => {
//         const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//         setTemporarySelections(updatedSelections);
//         setForm((prevForm) => ({
//           ...prevForm,
//           permission_location: updatedSelections,
//         }));
//         toast.success('Location removed!', { icon: '🗑️' });
//       }, 300);
//     }
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'All cities';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//       <div 
//         ref={formRef}
//         className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-2xl overflow-hidden w-full max-w-4xl max-h-[90vh] overflow-y-auto"
//       >
//         {/* Header with gradient */}
//         <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
//           <div className="flex justify-between items-center">
//             <div className="flex items-center space-x-3">
//               <Building className="text-white" size={24} />
//               <h1 className="text-2xl font-bold text-white">
//                 {editingCompany ? 'Edit Company' : 'Create New Company'}
//               </h1>
//             </div>
//             <button 
//               onClick={onClose}
//               className="p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-all"
//               aria-label="Close"
//             >
//               <X className="text-white" size={20} />
//             </button>
//           </div>
//         </div>

//         <ToastContainer 
//           position="top-right"
//           autoClose={2000}
//           hideProgressBar
//           newestOnTop
//           closeOnClick
//           pauseOnFocusLoss={false}
//           toastClassName="shadow-lg"
//         />

//         <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">
//           {/* Company Details */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             {/* Company Name */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 <Globe className="mr-2 text-blue-600" size={16} />
//                 Company Name <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <input
//                   type="text"
//                   value={form.company_name}
//                   onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//                   className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white shadow-sm group-hover:shadow-md"
//                   placeholder="Acme Inc."
                  
//                 />
//                 <Building className="absolute left-3 top-3.5 text-gray-400" size={18} />
//               </div>
//             </div>

//             {/* Company Email */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 <Mail className="mr-2 text-blue-600" size={16} />
//                 Company Email <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <input
//                   type="email"
//                   value={form.company_email}
//                   onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//                   className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white shadow-sm group-hover:shadow-md"
//                   placeholder="contact@company.com"
                  
//                 />
//                 <Mail className="absolute left-3 top-3.5 text-gray-400" size={18} />
//               </div>
//             </div>

//             {/* Company Expiry Date */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 <Calendar className="mr-2 text-blue-600" size={16} />
//                 Expiry Date
//               </label>
//               <div className="relative group">
//                 <input
//                   type="date"
//                   value={form.company_expiry}
//                   onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//                   className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white shadow-sm group-hover:shadow-md"
//                 />
//                 <Calendar className="absolute left-3 top-3.5 text-gray-400" size={18} />
//               </div>
//             </div>

//             {/* Password */}
//             <div className="flex flex-col space-y-2">
//               <label className="text-sm font-medium text-gray-700 flex items-center">
//                 <Lock className="mr-2 text-blue-600" size={16} />
//                 Password <span className="text-red-500 ml-1">*</span>
//               </label>
//               <div className="relative group">
//                 <input
//                   type="password"
//                   value={form.password}
//                   onChange={(e) => setForm({ ...form, password: e.target.value })}
//                   className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white shadow-sm group-hover:shadow-md"
//                   placeholder="••••••••"
                  
//                 />
//                 <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
//               </div>
//             </div>
//           </div>

//           {/* Location Selection */}
//           <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//               <MapPin className="mr-2 text-blue-600" size={20} />
//               Location Permissions
//             </h2>
            
//             <Selection
//               selectedCountry={selectedCountry}
//               setSelectedCountry={setSelectedCountry}
//               selectedState={selectedState}
//               setSelectedState={setSelectedState}
//               selectedCities={selectedCities}
//               setSelectedCities={setSelectedCities}
//               isCityDropdownOpen={isCityDropdownOpen}
//               setIsCityDropdownOpen={setIsCityDropdownOpen}
//               cityDropdownRef={cityDropdownRef}
//               isStateDropdownOpen={isStateDropdownOpen}
//               setIsStateDropdownOpen={setIsStateDropdownOpen}
//               stateDropdownRef={stateDropdownRef}
//             />

//             <div className="flex justify-end mt-4">
//               <button
//                 type="button"
//                 onClick={handleAddSelection}
//                 disabled={!selectedCountry}
//                 className={`flex items-center px-5 py-2.5 rounded-xl font-medium transition-all transform ${selectedCountry ? 
//                   'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5' : 
//                   'bg-gray-200 text-gray-500 cursor-not-allowed'} ${editingIndex !== null ? 'animate-pulse' : ''}`}
//               >
//                 {editingIndex !== null ? (
//                   <>
//                     <Check className="mr-2" size={18} />
//                     Update Location
//                   </>
//                 ) : (
//                   <>
//                     <Plus className="mr-2" size={18} />
//                     Add Location
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>

//           {/* Temporary Selections */}
//           <div className="space-y-4">
//             <h2 className="text-lg font-semibold text-gray-800 flex items-center">
//               <MapPin className="mr-2 text-blue-600" size={20} />
//               Selected Locations ({temporarySelections.length})
//             </h2>
            
//             {temporarySelections.length === 0 ? (
//               <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-xl border-2 border-dashed border-gray-300 text-center">
//                 <MapPin className="mx-auto text-gray-400" size={32} />
//                 <p className="text-gray-500 mt-2">No locations added yet. Add your first location above.</p>
//               </div>
//             ) : (
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 {temporarySelections.map((selection, index) => (
//                   <div 
//                     id={`location-card-${index}`}
//                     key={index} 
//                     className="location-card relative group p-5 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-blue-300 transition-all hover:shadow-md"
//                   >
//                     <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
//                       <button
//                         onClick={() => handleEditSelection(index)}
//                         className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors shadow-sm"
//                         title="Edit"
//                         aria-label="Edit location"
//                       >
//                         <Edit size={16} />
//                       </button>
//                       <button
//                         onClick={() => handleDeleteSelection(index)}
//                         className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors shadow-sm"
//                         title="Delete"
//                         aria-label="Delete location"
//                       >
//                         <Trash size={16} />
//                       </button>
//                     </div>
                    
//                     <div className="flex items-start space-x-4">
//                       <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-lg flex-shrink-0">
//                         <Globe className="h-5 w-5 text-blue-600" />
//                       </div>
                      
//                       <div className="flex-1 min-w-0">
//                         <h3 className="font-semibold text-gray-800 truncate">{selection.country}</h3>
//                         <div className="mt-2 space-y-1">
//                           <div className="flex items-center text-sm text-gray-600">
//                             <span className="font-medium w-16">State:</span>
//                             <span className="truncate">{selection.state || 'All states'}</span>
//                           </div>
//                           <div className="flex items-start text-sm text-gray-600">
//                             <span className="font-medium w-16">Cities:</span>
//                             <span className="truncate">{formatDisplay(selection.cities || [])}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* Form Buttons */}
//           <div className="flex justify-between pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={handleReset}
//               className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all flex items-center shadow-sm"
//             >
//               <X className="mr-2" size={16} />
//               Reset Form
//             </button>
//             <button
//               type="submit"
//               className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center shadow-lg hover:shadow-xl hover:-translate-y-0.5"
//             >
//               {editingCompany ? (
//                 <>
//                   <Edit className="mr-2" size={16} />
//                   Update Company
//                 </>
//               ) : (
//                 <>
//                   <Plus className="mr-2" size={16} />
//                   Create Company
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default CompanyManagementAdd;


// import React, { useState, useEffect, useRef } from 'react';
// import { Country, State, City } from 'country-state-city';
// import Select from 'react-select'; // Import Select from react-select
// import { Plus, Check, X, Edit, Trash } from 'lucide-react'; // Icons from lucide-react

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [], // Ensure this is always an array
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [error, setError] = useState('');
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null); // Track which temporary selection is being edited
//   const cityDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [], // Ensure it's an array
//       });

//       // Pre-fill temporary selections
//       setTemporarySelections(editingCompany.permission_location || []);

//       // Pre-fill country, state, and cities
//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         const country = Country.getAllCountries().find((c) => c.name === firstLocation.country);
//         const state = State.getStatesOfCountry(country?.isoCode).find((s) => s.name === firstLocation.state);

//         setSelectedCountry({ value: country?.isoCode, label: country?.name });
//         setSelectedState({ value: state?.isoCode, label: state?.name });
//         setSelectedCities(firstLocation.cities?.map((city) => ({ value: city, label: city })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // console.log(editingCompany,  "ye hai edit company vala data ")

//   // Close city dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Get all countries
//   const countries = Country.getAllCountries().map((country) => ({
//     value: country.isoCode,
//     label: country.name,
//   }));

//   // Get states based on selected country
//   const states = selectedCountry
//     ? State.getStatesOfCountry(selectedCountry.value).map((state) => ({
//       value: state.isoCode,
//       label: state.name,
//     }))
//     : [];

//   // Get cities based on selected state
//   const cities = selectedState
//     ? City.getCitiesOfState(selectedCountry?.value, selectedState?.value).map((city) => ({
//       value: city.name,
//       label: city.name,
//     }))
//     : [];

//   // Handle country selection
//   const handleCountryChange = (selectedOption) => {
//     setSelectedCountry(selectedOption);
//     setSelectedState(null);
//     setSelectedCities([]);
//     setError('');
//   };

//   // Handle state selection
//   const handleStateChange = (selectedOption) => {
//     setSelectedState(selectedOption);
//     setSelectedCities([]);
//     setError('');
//   };

//   // Handle city selection (add city to selectedCities)
//   const handleAddCity = (city) => {
//     if (!selectedCities.some((c) => c.value === city.value)) {
//       setSelectedCities([...selectedCities, city]);
//       setError('');
//     }
//   };

//   // Handle city removal
//   const handleRemoveCity = (city) => {
//     setSelectedCities(selectedCities.filter((c) => c.value !== city.value));
//   };

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry || !selectedState || selectedCities.length === 0) {
//       setError('Please select a country, state, and at least one city.');
//       return;
//     }

//     const newSelection = {
//       country: selectedCountry.label,
//       state: selectedState.label,
//       cities: selectedCities.map((city) => city.label), // Ensure this remains an array
//     };

//     if (editingIndex !== null) {
//       // Update the existing selection if editing
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null); // Reset editing index
//     } else {
//       // Add new selection
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: temporarySelections,
//     }));

//     resetFields();
//     setShowSuccess(true);
//     setTimeout(() => setShowSuccess(false), 3000); // Hide success message after 3 seconds
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//     setError('');
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       setError('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(), // Use existing ID if editing
//       ...form,
//       permission_location: temporarySelections, // Use the updated temporary selections
//     };

//     addCompanyData(newCompany); // Add or update company data
//     handleReset();
//     onClose(); // Close the modal after submission
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [], // Reset to an empty array
//     });
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//     setTemporarySelections([]);
//     setError('');
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     const country = countries.find((c) => c.label === selection.country);
//     const state = states.find((s) => s.label === selection.state);

//     setSelectedCountry(country);
//     setSelectedState(state);
//     setSelectedCities(selection.cities.map((city) => ({ value: city, label: city })));
//     setEditingIndex(index); // Set the index being edited
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <div className="space-y-6">
//           {/* Country, State, City in Row */}
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//             {/* Country */}
//             <div className="flex flex-col relative">
//               <label className="text-sm font-semibold mb-1 text-gray-700">Country</label>
//               <div className="flex items-center gap-2">
//                 <Select
//                   options={countries}
//                   value={selectedCountry}
//                   onChange={handleCountryChange}
//                   placeholder="Select country..."
//                   className="react-select-container flex-1"
//                   classNamePrefix="react-select"
//                   isClearable
//                 />
//               </div>
//             </div>

//             {/* State */}
//             <div className="flex flex-col relative">
//               <label className="text-sm font-semibold mb-1 text-gray-700">State</label>
//               <div className="flex items-center gap-2">
//                 <Select
//                   options={states}
//                   value={selectedState}
//                   onChange={handleStateChange}
//                   placeholder="Select state..."
//                   className="react-select-container flex-1"
//                   classNamePrefix="react-select"
//                   isDisabled={!selectedCountry}
//                   isClearable
//                 />
//               </div>
//             </div>

//             {/* City */}
//             <div className="flex flex-col relative">
//               <label className="text-sm font-semibold mb-1 text-gray-700">City</label>
//               <div className="flex items-center gap-2">
//                 <div className="relative flex-1" ref={cityDropdownRef}>
//                   <input
//                     type="text"
//                     value={selectedCities.map((city) => city.label).join(', ')}
//                     readOnly
//                     onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
//                     className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full cursor-pointer"
//                     placeholder="Select cities..."
//                   />
//                   {isCityDropdownOpen && (
//                     <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
//                       {cities.map((city) => (
//                         <div
//                           key={city.value}
//                           className="p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
//                           onClick={() => handleAddCity(city)}
//                         >
//                           <span>{city.label}</span>
//                           {selectedCities.some((c) => c.value === city.value) && (
//                             <X
//                               className="text-red-500 hover:text-red-700"
//                               size={16}
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 handleRemoveCity(city);
//                               }}
//                             />
//                           )}
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//                 <button
//                   type="button"
//                   onClick={handleAddSelection}
//                   className=" bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center p-1"
//                 >
//                   <Plus className="mr" size={16} />
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Success and Error Messages */}
//         {showSuccess && (
//           <div className="p-3 bg-green-100 text-green-700 rounded-md flex items-center">
//             <Check className="mr-2" size={16} /> Selection added successfully!
//           </div>
//         )}
//         {error && (
//           <div className="p-3 bg-red-100 text-red-700 rounded-md flex items-center">
//             {error}
//           </div>
//         )}

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   {/* Delete and Edit Icons */}
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}, {selection.state}
//                   </p>
//                   {/* <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong>
//                     {Array.isArray(selection.cities)
//                       ? selection.cities.map((city) => city.label).join(', ')
//                       : 'N/A'}
//                   </p> */}

//                   <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong> {selection.cities.join(', ')}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;


// import React, { useState, useEffect, useRef } from 'react';
// import { Country, State, City } from 'country-state-city';
// import { Plus, Check, X, Edit, Trash } from 'lucide-react';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [error, setError] = useState('');
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         const country = Country.getAllCountries().find((c) => c.name === firstLocation.country);
//         const states = firstLocation.states || [firstLocation.state].filter(Boolean);
        
//         setSelectedCountry({ value: country?.isoCode, label: country?.name });
        
//         const stateOptions = State.getStatesOfCountry(country?.isoCode) || [];
//         const selectedStateValues = states.map(stateName => {
//           const state = stateOptions.find(s => s.name === stateName);
//           return state ? { value: state.isoCode, label: state.name } : null;
//         }).filter(Boolean);

//         setSelectedStates(selectedStateValues);
        
//         const cityOptions = selectedStateValues.flatMap(state => {
//           const cities = City.getCitiesOfState(country?.isoCode, state.value) || [];
//           return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//         });
        
//         setSelectedCities(firstLocation.cities?.map(cityName => {
//           const city = cityOptions.find(c => c.label === cityName);
//           return city || { value: cityName, label: cityName };
//         }) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close city dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry || selectedStates.length === 0 || selectedCities.length === 0) {
//       setError('Please select a country, at least one state, and cities.');
//       return;
//     }

//     const newSelection = {
//       country: selectedCountry.label,
//       states: selectedStates.map(state => state.label),
//       cities: selectedCities.map(city => city.label),
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//     setShowSuccess(true);
//     setTimeout(() => setShowSuccess(false), 3000);
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedStates([]);
//     setSelectedCities([]);
//     setError('');
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       setError('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     const countries = Country.getAllCountries().map((country) => ({
//       value: country.isoCode,
//       label: country.name,
//     }));
//     const country = countries.find((c) => c.label === selection.country);
    
//     const stateOptions = State.getStatesOfCountry(country?.value) || [];
//     const selectedStateValues = (selection.states || [selection.state].filter(Boolean)).map(stateName => {
//       const state = stateOptions.find(s => s.name === stateName);
//       return state ? { value: state.isoCode, label: state.name } : null;
//     }).filter(Boolean);

//     const cityOptions = selectedStateValues.flatMap(state => {
//       const cities = City.getCitiesOfState(country?.value, state.value) || [];
//       return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//     });

//     setSelectedCountry(country);
//     setSelectedStates(selectedStateValues);
//     setSelectedCities(selection.cities?.map(cityName => {
//       const city = cityOptions.find(c => c.label === cityName);
//       return city || { value: cityName, label: cityName };
//     }) || []);
//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//   };

//   // Format cities display with ellipsis after 4 cities
//   const formatCitiesDisplay = (cities) => {
//     if (!cities || cities.length === 0) return 'No cities selected';
//     const displayedCities = cities.slice(0, 4);
//     return `${displayedCities.join(', ')}${cities.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedStates={selectedStates}
//           setSelectedStates={setSelectedStates}
//           selectedCities={selectedCities}
//           setSelectedCities={setSelectedCities}
//           isCityDropdownOpen={isCityDropdownOpen}
//           setIsCityDropdownOpen={setIsCityDropdownOpen}
//           cityDropdownRef={cityDropdownRef}
//           handleAddSelection={handleAddSelection}
//           error={error}
//           setError={setError}
//           editingIndex={editingIndex}
//         />

//         {/* Success Message */}
//         {showSuccess && (
//           <div className="p-3 bg-green-100 text-green-700 rounded-md flex items-center">
//             <Check className="mr-2" size={16} /> Selection {editingIndex !== null ? 'updated' : 'added'} successfully!
//           </div>
//         )}

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>States:</strong> {formatCitiesDisplay(selection.states || [selection.state].filter(Boolean))}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong> {formatCitiesDisplay(selection.cities)}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;




// import React, { useState, useEffect, useRef } from 'react';
// import { Country, State, City } from 'country-state-city';
// import { Plus, Check, X, Edit, Trash } from 'lucide-react';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [error, setError] = useState('');
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         const country = Country.getAllCountries().find((c) => c.name === firstLocation.country);
//         const states = firstLocation.states || [firstLocation.state].filter(Boolean);
        
//         setSelectedCountry({ value: country?.isoCode, label: country?.name });
        
//         const stateOptions = State.getStatesOfCountry(country?.isoCode) || [];
//         const selectedStateValues = states.map(stateName => {
//           const state = stateOptions.find(s => s.name === stateName);
//           return state ? { value: state.isoCode, label: state.name } : null;
//         }).filter(Boolean);

//         setSelectedStates(selectedStateValues);
        
//         const cityOptions = selectedStateValues.flatMap(state => {
//           const cities = City.getCitiesOfState(country?.isoCode, state.value) || [];
//           return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//         });
        
//         setSelectedCities(firstLocation.cities?.map(cityName => {
//           const city = cityOptions.find(c => c.label === cityName);
//           return city || { value: cityName, label: cityName };
//         }) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     if (!selectedCountry || selectedStates.length === 0 || selectedCities.length === 0) {
//       setError('Please select a country, at least one state, and cities.');
//       return;
//     }

//     const newSelection = {
//       country: selectedCountry.label,
//       states: selectedStates.map(state => state.label),
//       cities: selectedCities.map(city => city.label),
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//     setShowSuccess(true);
//     setTimeout(() => setShowSuccess(false), 3000);
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedStates([]);
//     setSelectedCities([]);
//     setError('');
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       setError('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     const countries = Country.getAllCountries().map((country) => ({
//       value: country.isoCode,
//       label: country.name,
//     }));
//     const country = countries.find((c) => c.label === selection.country);
    
//     const stateOptions = State.getStatesOfCountry(country?.value) || [];
//     const selectedStateValues = (selection.states || [selection.state].filter(Boolean)).map(stateName => {
//       const state = stateOptions.find(s => s.name === stateName);
//       return state ? { value: state.isoCode, label: state.name } : null;
//     }).filter(Boolean);

//     const cityOptions = selectedStateValues.flatMap(state => {
//       const cities = City.getCitiesOfState(country?.value, state.value) || [];
//       return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//     });

//     setSelectedCountry(country);
//     setSelectedStates(selectedStateValues);
//     setSelectedCities(selection.cities?.map(cityName => {
//       const city = cityOptions.find(c => c.label === cityName);
//       return city || { value: cityName, label: cityName };
//     }) || []);
//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedStates={selectedStates}
//           setSelectedStates={setSelectedStates}
//           selectedCities={selectedCities}
//           setSelectedCities={setSelectedCities}
//           isCityDropdownOpen={isCityDropdownOpen}
//           setIsCityDropdownOpen={setIsCityDropdownOpen}
//           cityDropdownRef={cityDropdownRef}
//           isStateDropdownOpen={isStateDropdownOpen}
//           setIsStateDropdownOpen={setIsStateDropdownOpen}
//           stateDropdownRef={stateDropdownRef}
//           handleAddSelection={handleAddSelection}
//           error={error}
//           setError={setError}
//           editingIndex={editingIndex}
//         />

//         {/* Success Message */}
//         {showSuccess && (
//           <div className="p-3 bg-green-100 text-green-700 rounded-md flex items-center">
//             <Check className="mr-2" size={16} /> Selection {editingIndex !== null ? 'updated' : 'added'} successfully!
//           </div>
//         )}

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>States:</strong> {formatDisplay(selection.states || [selection.state].filter(Boolean))}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong> {formatDisplay(selection.cities)}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;




// new code start here 
// import React, { useState, useEffect, useRef } from 'react';
// import { Country, State, City } from 'country-state-city';
// import { Plus, Edit, Trash } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         const country = Country.getAllCountries().find((c) => c.name === firstLocation.country);
//         const states = firstLocation.states || [firstLocation.state].filter(Boolean);
        
//         setSelectedCountry({ value: country?.isoCode, label: country?.name });
        
//         const stateOptions = State.getStatesOfCountry(country?.isoCode) || [];
//         const selectedStateValues = states.map(stateName => {
//           const state = stateOptions.find(s => s.name === stateName);
//           return state ? { value: state.isoCode, label: state.name } : null;
//         }).filter(Boolean);

//         setSelectedStates(selectedStateValues);
        
//         const cityOptions = selectedStateValues.flatMap(state => {
//           const cities = City.getCitiesOfState(country?.isoCode, state.value) || [];
//           return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//         });
        
//         setSelectedCities(firstLocation.cities?.map(cityName => {
//           const city = cityOptions.find(c => c.label === cityName);
//           return city || { value: cityName, label: cityName };
//         }) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     const newSelection = {
//       country: selectedCountry.label,
//       states: selectedStates.map(state => state.label),
//       cities: selectedCities.map(city => city.label),
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedStates([]);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       toast.error('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     const countries = Country.getAllCountries().map((country) => ({
//       value: country.isoCode,
//       label: country.name,
//     }));
//     const country = countries.find((c) => c.label === selection.country);
    
//     const stateOptions = State.getStatesOfCountry(country?.value) || [];
//     const selectedStateValues = (selection.states || [selection.state].filter(Boolean)).map(stateName => {
//       const state = stateOptions.find(s => s.name === stateName);
//       return state ? { value: state.isoCode, label: state.name } : null;
//     }).filter(Boolean);

//     const cityOptions = selectedStateValues.flatMap(state => {
//       const cities = City.getCitiesOfState(country?.value, state.value) || [];
//       return cities.map(city => ({ value: city.name, label: city.name, state: state.label }));
//     });

//     setSelectedCountry(country);
//     setSelectedStates(selectedStateValues);
//     setSelectedCities(selection.cities?.map(cityName => {
//       const city = cityOptions.find(c => c.label === cityName);
//       return city || { value: cityName, label: cityName };
//     }) || []);
//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Selection deleted successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <ToastContainer position="top-right" autoClose={1000} />
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedStates={selectedStates}
//           setSelectedStates={setSelectedStates}
//           selectedCities={selectedCities}
//           setSelectedCities={setSelectedCities}
//           isCityDropdownOpen={isCityDropdownOpen}
//           setIsCityDropdownOpen={setIsCityDropdownOpen}
//           cityDropdownRef={cityDropdownRef}
//           isStateDropdownOpen={isStateDropdownOpen}
//           setIsStateDropdownOpen={setIsStateDropdownOpen}
//           stateDropdownRef={stateDropdownRef}
//           handleAddSelection={handleAddSelection}
//           editingIndex={editingIndex}
//         />

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>States:</strong> {formatDisplay(selection.states || [selection.state].filter(Boolean))}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>Cities:</strong> {formatDisplay(selection.cities)}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;




// new code ...... 
// import React, { useState, useEffect, useRef } from 'react';
// import { Country, State } from 'country-state-city';
// import { Plus, Edit, Trash } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedDistricts, setSelectedDistricts] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isDistrictDropdownOpen, setIsDistrictDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const districtDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         const country = Country.getAllCountries().find((c) => c.name === firstLocation.country);
//         const states = firstLocation.states || [firstLocation.state].filter(Boolean);
        
//         setSelectedCountry({ value: country?.isoCode, label: country?.name });
        
//         const stateOptions = State.getStatesOfCountry(country?.isoCode) || [];
//         const selectedStateValues = states.map(stateName => {
//           const state = stateOptions.find(s => s.name === stateName);
//           return state ? { value: state.isoCode, label: state.name } : null;
//         }).filter(Boolean);

//         setSelectedStates(selectedStateValues);
        
//         setSelectedDistricts(firstLocation.districts?.map(districtName => ({
//           value: districtName,
//           label: districtName
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (districtDropdownRef.current && !districtDropdownRef.current.contains(event.target)) {
//         setIsDistrictDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     const newSelection = {
//       country: selectedCountry.label,
//       states: selectedStates.map(state => state.label),
//       districts: selectedDistricts.map(district => district.label),
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedStates([]);
//     setSelectedDistricts([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       toast.error('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
//     const countries = Country.getAllCountries().map((country) => ({
//       value: country.isoCode,
//       label: country.name,
//     }));
//     const country = countries.find((c) => c.label === selection.country);
    
//     const stateOptions = State.getStatesOfCountry(country?.value) || [];
//     const selectedStateValues = (selection.states || [selection.state].filter(Boolean)).map(stateName => {
//       const state = stateOptions.find(s => s.name === stateName);
//       return state ? { value: state.isoCode, label: state.name } : null;
//     }).filter(Boolean);

//     setSelectedCountry(country);
//     setSelectedStates(selectedStateValues);
//     setSelectedDistricts(selection.districts?.map(districtName => ({
//       value: districtName,
//       label: districtName
//     })) || []);
//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Selection deleted successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <ToastContainer position="top-right" autoClose={1000} />
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedStates={selectedStates}
//           setSelectedStates={setSelectedStates}
//           selectedDistricts={selectedDistricts}
//           setSelectedDistricts={setSelectedDistricts}
//           isDistrictDropdownOpen={isDistrictDropdownOpen}
//           setIsDistrictDropdownOpen={setIsDistrictDropdownOpen}
//           districtDropdownRef={districtDropdownRef}
//           isStateDropdownOpen={isStateDropdownOpen}
//           setIsStateDropdownOpen={setIsStateDropdownOpen}
//           stateDropdownRef={stateDropdownRef}
//           handleAddSelection={handleAddSelection}
//           editingIndex={editingIndex}
//         />

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>States:</strong> {formatDisplay(selection.states || [selection.state].filter(Boolean))}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>Districts:</strong> {formatDisplay(selection.districts)}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;




// new code according to selection section changes geo......... 
// import React, { useState, useEffect, useRef } from 'react';
// import { Plus, Edit, Trash } from 'lucide-react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import Selection from '../Management/Company/ManageCountryselection/selection';

// const CompanyManagementAdd = ({ onClose, addCompanyData, editingCompany }) => {
//   const [form, setForm] = useState({
//     company_name: '',
//     company_email: '',
//     company_expiry: '',
//     password: '',
//     permission_location: [],
//   });

//   const [selectedCountry, setSelectedCountry] = useState(null);
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCities, setSelectedCities] = useState([]);
//   const [temporarySelections, setTemporarySelections] = useState([]);
//   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
//   const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
//   const [editingIndex, setEditingIndex] = useState(null);
//   const cityDropdownRef = useRef(null);
//   const stateDropdownRef = useRef(null);

//   // Pre-fill form if editingCompany is provided
//   useEffect(() => {
//     if (editingCompany) {
//       setForm({
//         company_name: editingCompany.company_name,
//         company_email: editingCompany.company_email,
//         company_expiry: editingCompany.company_expiry,
//         password: editingCompany.password,
//         permission_location: editingCompany.permission_location || [],
//       });

//       setTemporarySelections(editingCompany.permission_location || []);

//       const firstLocation = editingCompany.permission_location?.[0];
//       if (firstLocation) {
//         setSelectedCountry({ 
//           value: firstLocation.countryCode || firstLocation.country, 
//           label: firstLocation.country 
//         });
        
//         setSelectedState(firstLocation.state ? { 
//           value: firstLocation.state, 
//           label: firstLocation.state 
//         } : null);

//         setSelectedCities(firstLocation.cities?.map(city => ({
//           value: city,
//           label: city
//         })) || []);
//       }
//     }
//   }, [editingCompany]);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
//         setIsStateDropdownOpen(false);
//       }
//       if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
//         setIsCityDropdownOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Add or update temporary selection
//   const handleAddSelection = () => {
//     const newSelection = {
//       country: selectedCountry.label,
//       state: selectedState.label,
//       cities: selectedCities.map(city => city.label)
//     };

//     if (editingIndex !== null) {
//       const updatedSelections = [...temporarySelections];
//       updatedSelections[editingIndex] = newSelection;
//       setTemporarySelections(updatedSelections);
//       setEditingIndex(null);
//     } else {
//       setTemporarySelections([...temporarySelections, newSelection]);
//     }

//     resetFields();
//   };

//   // Reset fields after adding to temporary selections
//   const resetFields = () => {
//     setSelectedCountry(null);
//     setSelectedState(null);
//     setSelectedCities([]);
//   };

//   // Handle final submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (temporarySelections.length === 0) {
//       toast.error('Please add at least one location before submitting.');
//       return;
//     }

//     const newCompany = {
//       id: editingCompany ? editingCompany.id : Date.now(),
//       ...form,
//       permission_location: temporarySelections,
//     };

//     addCompanyData(newCompany);
//     handleReset();
//     toast.success(`Company ${editingCompany ? 'updated' : 'added'} successfully!`);
//     onClose();
//   };

//   // Reset form fields
//   const handleReset = () => {
//     setForm({
//       company_name: '',
//       company_email: '',
//       company_expiry: '',
//       password: '',
//       permission_location: [],
//     });
//     resetFields();
//     setTemporarySelections([]);
//   };

//   // Handle edit selection
//   const handleEditSelection = (index) => {
//     const selection = temporarySelections[index];
    
//     setSelectedCountry({ 
//       value: selection.countryCode || selection.country, 
//       label: selection.country 
//     });
    
//     setSelectedState(selection.state ? { 
//       value: selection.state, 
//       label: selection.state 
//     } : null);

//     setSelectedCities(selection.cities?.map(city => ({
//       value: city,
//       label: city
//     })) || []);

//     setEditingIndex(index);
//   };

//   // Handle delete selection
//   const handleDeleteSelection = (index) => {
//     const updatedSelections = temporarySelections.filter((_, i) => i !== index);
//     setTemporarySelections(updatedSelections);
//     setForm((prevForm) => ({
//       ...prevForm,
//       permission_location: updatedSelections,
//     }));
//     toast.success('Selection deleted successfully!');
//   };

//   // Format display with ellipsis after 4 items
//   const formatDisplay = (items) => {
//     if (!items || items.length === 0) return 'None selected';
//     const displayedItems = items.slice(0, 4);
//     return `${displayedItems.join(', ')}${items.length > 4 ? '...' : ''}`;
//   };

//   return (
//     <div className="p-6 bg-white rounded-lg shadow-lg">
//       <ToastContainer position="top-right" autoClose={1000} />
//       <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
//         {editingCompany ? 'Edit Company' : 'Add Company'}
//       </h1>
//       <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
//         {/* Company Details */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Company Name */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Name</label>
//             <input
//               type="text"
//               value={form.company_name}
//               onChange={(e) => setForm({ ...form, company_name: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company name"
//             />
//           </div>

//           {/* Company Email */}
//           <div className="flex flex-col ">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Email</label>
//             <input
//               type="email"
//               value={form.company_email}
//               onChange={(e) => setForm({ ...form, company_email: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter company email"
//             />
//           </div>

//           {/* Company Expiry Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Company Expiry Date</label>
//             <input
//               type="date"
//               value={form.company_expiry}
//               onChange={(e) => setForm({ ...form, company_expiry: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold mb-1 text-gray-700">Password</label>
//             <input
//               type="password"
//               value={form.password}
//               onChange={(e) => setForm({ ...form, password: e.target.value })}
//               className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               placeholder="Enter password"
//             />
//           </div>
//         </div>

//         {/* Location Selection */}
//         <Selection
//           selectedCountry={selectedCountry}
//           setSelectedCountry={setSelectedCountry}
//           selectedState={selectedState}
//           setSelectedState={setSelectedState}
//           selectedCities={selectedCities}
//           setSelectedCities={setSelectedCities}
//           isCityDropdownOpen={isCityDropdownOpen}
//           setIsCityDropdownOpen={setIsCityDropdownOpen}
//           cityDropdownRef={cityDropdownRef}
//           isStateDropdownOpen={isStateDropdownOpen}
//           setIsStateDropdownOpen={setIsStateDropdownOpen}
//           stateDropdownRef={stateDropdownRef}
//           handleAddSelection={handleAddSelection}
//           editingIndex={editingIndex}
//         />

//         {/* Temporary Selections */}
//         <div className="space-y-4">
//           <h2 className="text-lg font-semibold text-gray-700">Temporary Selections</h2>
//           {temporarySelections.length === 0 ? (
//             <p className="text-gray-500">No selections added yet.</p>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {temporarySelections.map((selection, index) => (
//                 <div key={index} className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 relative">
//                   <div className="absolute top-2 right-2 flex gap-2">
//                     <Edit
//                       className="text-blue-500 hover:text-blue-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleEditSelection(index)}
//                     />
//                     <Trash
//                       className="text-red-500 hover:text-red-700 cursor-pointer"
//                       size={16}
//                       onClick={() => handleDeleteSelection(index)}
//                     />
//                   </div>
//                   <p className="font-semibold text-gray-800">
//                     {selection.country}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>State:</strong> {selection.state || 'None selected'}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <strong>District:</strong> {formatDisplay(selection.cities || [])}
//                   </p>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Form Buttons */}
//         <div className="flex justify-between">
//           <button
//             type="button"
//             onClick={handleReset}
//             className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
//           >
//             Reset
//           </button>
//           <button
//             type="submit"
//             className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editingCompany ? 'Update' : 'Submit'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CompanyManagementAdd;
